'use client';

import { useState } from 'react';
import { useTranslations } from 'use-intl';
import Drawer from '@/components/organisms/drawer';
import Button from '@/components/atoms/button';
import type { PurchaseListItem } from '@/types/entity/purchase-list';
import type { ListSummary } from '../list-switcher';

interface MoveItemDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  item: PurchaseListItem | null;
  destinationLists: ListSummary[];
  onSubmit: (targetListId: string) => Promise<boolean>;
}

const MoveItemDrawer = ({ isOpen, onClose, item, destinationLists, onSubmit }: MoveItemDrawerProps) => {
  const translate = useTranslations('dashboard');
  const translateCommon = useTranslations('common');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    setSelectedId(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    const ok = await onSubmit(selectedId);
    setSubmitting(false);
    if (ok) {
      setSelectedId(null);
      onClose();
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} headline={translate('purchase-list-move-item')} direction="right">
      <div className="flex min-h-full flex-col">
        {item && (
          <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
            <p className="text-13 tracking-wide text-gray-500 uppercase">{translate('purchase-list-move-moving')}</p>
            <p className="text-15 mt-1 line-clamp-2 font-semibold text-gray-800">{item.name}</p>
            <p className="text-12 mt-0.5 text-gray-400">
              {translateCommon('cat-no')}: {item.sku}
            </p>
          </div>
        )}

        <div className="flex-1 px-6 py-4">
          <p className="text-13 mb-3 font-semibold tracking-wide text-gray-600 uppercase">
            {translate('purchase-list-move-destination')}
          </p>

          {destinationLists.length === 0 ? (
            <p className="text-13 text-gray-500">{translate('purchase-list-move-no-lists')}</p>
          ) : (
            <div className="flex flex-col gap-2">
              {destinationLists.map((list) => (
                <button
                  key={list.id}
                  type="button"
                  onClick={() => setSelectedId(list.id)}
                  className={[
                    'flex w-full flex-col items-start rounded-lg border-2 px-4 py-3 text-left transition-all',
                    selectedId === list.id
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 bg-white hover:border-gray-300',
                  ].join(' ')}
                >
                  <span
                    className={`text-15 font-semibold ${selectedId === list.id ? 'text-primary' : 'text-gray-800'}`}
                  >
                    {list.name}
                  </span>
                  <span className="text-12 text-gray-500">
                    {translate('purchase-list-item-count', { count: list.itemCount })}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 border-t border-gray-200 bg-gray-50 px-6 py-4">
          <Button
            type="button"
            variant="primary"
            size="m"
            className="w-full"
            onClick={handleSubmit}
            loading={submitting}
            disabled={!selectedId}
          >
            {translate('purchase-list-move-confirm')}
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default MoveItemDrawer;
